import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, RotateCcw, Layers, Trophy, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const GRID_SIZE = 5;
const MAX_HEIGHT = 5;

// Isometric math helper
// Projects grid coordinates (x, y, z) to screen coordinates (screenX, screenY)
// Origin (0,0,0) is at the top corner of the diamond.
const toIso = (x, y, z) => {
    const tileWidth = 40;
    const tileHeight = 20; // Flattened for perspective
    const layerHeight = 20; // Height of one block

    const screenX = (x - y) * tileWidth;
    const screenY = (x + y) * tileHeight - (z * layerHeight);
    
    return { x: screenX, y: screenY };
};

const IsometricBuilderGame = () => {
    const navigate = useNavigate();
    const [level, setLevel] = useState(1);
    const [targetGrid, setTargetGrid] = useState(Array(GRID_SIZE).fill().map(() => Array(GRID_SIZE).fill(0)));
    const [userGrid, setUserGrid] = useState(Array(GRID_SIZE).fill().map(() => Array(GRID_SIZE).fill(0)));
    const [gameState, setGameState] = useState('playing'); // playing, won
    const [showRules, setShowRules] = useState(false);

    // Initialize/Reset Game
    const startNewGame = (currentLevel) => {
        // Generate random target grid based on level intensity
        const newTarget = Array(GRID_SIZE).fill().map(() => Array(GRID_SIZE).fill(0));
        
        // Simple generation: Add 'level + 2' random blocks
        let blocksToAdd = currentLevel + 3;
        while (blocksToAdd > 0) {
            const r = Math.floor(Math.random() * GRID_SIZE);
            const c = Math.floor(Math.random() * GRID_SIZE);
            if (newTarget[r][c] < MAX_HEIGHT) {
                newTarget[r][c]++;
                blocksToAdd--;
            }
        }
        
        setTargetGrid(newTarget);
        setUserGrid(Array(GRID_SIZE).fill().map(() => Array(GRID_SIZE).fill(0)));
        setGameState('playing');
    };

    useEffect(() => {
        startNewGame(level);
    }, [level]);

    // Check for win
    useEffect(() => {
        const targetTotal = targetGrid.flat().reduce((a, b) => a + b, 0);
        const isWin = targetTotal > 0 && targetGrid.every((row, r) => 
            row.every((val, c) => val === userGrid[r][c])
        );
        if (isWin && gameState !== 'won') {
            setGameState('won');
        }
    }, [userGrid, targetGrid, gameState]);

    const handleInteraction = (r, c, type) => {
        if (gameState === 'won') return;
        
        const newGrid = userGrid.map(row => [...row]);
        if (type === 'add' && newGrid[r][c] < MAX_HEIGHT) {
            newGrid[r][c]++;
        } else if (type === 'remove' && newGrid[r][c] > 0) {
            newGrid[r][c]--;
        }
        setUserGrid(newGrid);
    };

    // Render a single isometric block face
    const Block = ({ x, y, z, color, topColor, sideColor, onClick, onRightClick, isInteractive }) => {
        const pos = toIso(x, y, z);
        
        return (
            <g 
                transform={`translate(${pos.x}, ${pos.y})`}
                onClick={onClick}
                onContextMenu={onRightClick}
                className={isInteractive ? "cursor-pointer hover:opacity-90" : ""}
            >
                {/* Left Face */}
                <path d="M 0 20 L -40 0 L -40 -40 L 0 -20 Z" fill={sideColor || "#2563eb"} stroke="rgba(0,0,0,0.1)" />
                {/* Right Face */}
                <path d="M 0 20 L 40 0 L 40 -40 L 0 -20 Z" fill={sideColor || "#1d4ed8"} filter="brightness(0.9)" stroke="rgba(0,0,0,0.1)" />
                {/* Top Face */}
                <path d="M 0 -20 L 40 -40 L 0 -60 L -40 -40 Z" fill={topColor || "#3b82f6"} stroke="rgba(255,255,255,0.2)" />
            </g>
        );
    };

    // Render Grid Helper
    const RenderGrid = ({ gridData, isInteractive = false }) => {
        // We must render from back (0,0) to front (4,4) to handle occlusion correctly
        // Sort order: (x + y) ascending.
        
        const cells = [];
        for(let x=0; x<GRID_SIZE; x++){
            for(let y=0; y<GRID_SIZE; y++){
                cells.push({x, y, h: gridData[x][y]});
            }
        }
        
        // Sort by depth (painters algorithm)
        // In iso, x+y is the main depth factor.
        cells.sort((a,b) => (a.x + a.y) - (b.x + b.y));

        return (
            <g transform="translate(400, 200)"> {/* Center the grid */}
                {/* Base Grid Plane (always draw at z=0) */}
                {cells.map(cell => {
                    const pos = toIso(cell.x, cell.y, 0);
                    return (
                        <g 
                            key={`base-${cell.x}-${cell.y}`} 
                            transform={`translate(${pos.x}, ${pos.y})`}
                            onClick={() => isInteractive && handleInteraction(cell.x, cell.y, 'add')}
                            onContextMenu={(e) => {
                                e.preventDefault();
                                isInteractive && handleInteraction(cell.x, cell.y, 'remove');
                            }}
                            className={isInteractive ? "cursor-pointer hover:opacity-80" : ""}
                        >
                            {/* Flat base tile */}
                            <path d="M 0 20 L 40 0 L 0 -20 L -40 0 Z" fill="#e2e8f0" stroke="#cbd5e1" />
                            {/* Hover highlight for base */}
                            {isInteractive && <path d="M 0 20 L 40 0 L 0 -20 L -40 0 Z" fill="black" fillOpacity="0" className="hover:fill-opacity-10" />}
                        </g>
                    );
                })}

                {/* Blocks */}
                {cells.map(cell => {
                    const blocks = [];
                    for(let h=0; h<cell.h; h++) {
                        blocks.push(
                            <Block 
                                key={`block-${cell.x}-${cell.y}-${h}`}
                                x={cell.x} 
                                y={cell.y} 
                                z={h+1} // +1 so it sits ON the base
                                topColor={isInteractive ? "#f59e0b" : "#3b82f6"} 
                                sideColor={isInteractive ? "#d97706" : "#2563eb"}
                                isInteractive={isInteractive}
                                onClick={() => isInteractive && handleInteraction(cell.x, cell.y, 'add')}
                                onRightClick={(e) => {
                                    e.preventDefault();
                                    isInteractive && handleInteraction(cell.x, cell.y, 'remove');
                                }}
                            />
                        );
                    }
                    return blocks;
                })}
            </g>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center py-8">
            <div className="w-full max-w-6xl px-4 flex justify-between items-center mb-6">
                 <button onClick={() => navigate("/games")} className="flex items-center text-gray-600">
                    <ArrowLeft className="mr-1" /> Back to Games
                 </button>
                 <h1 className="text-3xl font-bold text-gray-800">Isometric Builder</h1>
                 <button onClick={() => startNewGame(level)} className="text-blue-600 flex items-center gap-1">
                    <RotateCcw size={20}/> Reset
                 </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 w-full max-w-6xl">
                {/* Target View */}
                <div className="bg-white p-6 rounded-xl shadow-lg flex flex-col items-center">
                    <h2 className="text-xl font-bold text-gray-700 mb-4 flex items-center gap-2">
                        <Layers className="text-blue-500" /> Target Structure
                    </h2>
                    <div className="border rounded-lg bg-gray-50 w-full h-80 flex items-center justify-center overflow-hidden">
                        <svg width="100%" height="100%" viewBox="0 0 800 600">
                             <RenderGrid gridData={targetGrid} isInteractive={false} />
                        </svg>
                    </div>
                    <p className="text-sm text-gray-500 mt-4">Match this shape exactly!</p>
                </div>

                {/* User Workspace */}
                <div className="bg-white p-6 rounded-xl shadow-lg flex flex-col items-center border-2 border-amber-100">
                    <div className="flex justify-between w-full mb-4 px-2">
                        <h2 className="text-xl font-bold text-gray-700 flex items-center gap-2">
                            <Layers className="text-amber-500" /> Your Build
                        </h2>
                        <div className="text-sm text-gray-500 flex items-center gap-2">
                            <span className="bg-gray-100 px-2 py-1 rounded">Left Click: Add</span>
                            <span className="bg-gray-100 px-2 py-1 rounded">Right Click: Remove</span>
                        </div>
                    </div>
                    
                    <div className="border rounded-lg bg-amber-50/30 w-full h-80 flex items-center justify-center overflow-hidden relative">
                        <svg width="100%" height="100%" viewBox="0 0 800 600">
                             <RenderGrid gridData={userGrid} isInteractive={true} />
                        </svg>
                        
                        {gameState === 'won' && (
                            <div className="absolute inset-0 bg-white/80 flex flex-col items-center justify-center backdrop-blur-sm">
                                <Trophy className="w-16 h-16 text-yellow-500 mb-4 animate-bounce" />
                                <h2 className="text-3xl font-bold text-gray-800 mb-2">Perfect match!</h2>
                                <button 
                                    onClick={() => setLevel(level + 1)}
                                    className="bg-blue-600 text-white px-6 py-3 rounded-lg font-bold shadow-lg hover:bg-blue-700 transition-colors"
                                >
                                    Next Level
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            <div className="mt-8 bg-blue-50 p-4 rounded-lg flex items-start gap-3 max-w-2xl">
                <Info className="flex-shrink-0 text-blue-500" />
                <div>
                     <h3 className="font-bold text-blue-900 text-sm">How to play</h3>
                     <p className="text-sm text-blue-800 mt-1">
                        Recreate the blue structure on the left by building with orange blocks on the right. 
                        Click grid cells to stack blocks upwards. Right-click to remove them.
                        You must match the height of every stack exactly.
                     </p>
                </div>
            </div>
        </div>
    );
};

export default IsometricBuilderGame;
